// src/components/AccountInfoView.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import type { UserProfile } from '../types';
import { auth } from '../lib/firebase';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';
import {
  getRoddoAccountIdentity,
  linkRoddoAccountProvider,
  linkRoddoEmailPassword,
  type RoddoAccountIdentity,
  type RoddoLinkProvider,
} from '../services/accountLinking';
import { requestAccountDeletion } from '../services/accountDeletionRequests';
import { updateUserProfile } from '../services/users';
import {
  formatRoddoSupportDiagnostics,
  getRoddoSupportDiagnostics,
  supportVersionLabel,
  type RoddoSupportDiagnostics,
} from '../services/supportDiagnostics';

type ToastType = 'success' | 'error' | 'info';

type Props = {
  userProfile: UserProfile;
  isDarkMode?: boolean;
  onBack: () => void;
  onLogout: () => void;
  addToast?: (message: string, type?: ToastType) => void;
};

function toMillis(value: any): number {
  if (!value) return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value?.toMillis === 'function') {
    try {
      const n = Number(value.toMillis());
      return Number.isFinite(n) ? n : 0;
    } catch {}
  }
  if (typeof value?.seconds === 'number') return Number(value.seconds) * 1000;
  if (value instanceof Date) return value.getTime();
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function formatDate(value: any): string {
  const ms = toMillis(value);
  if (!ms) return 'Not available';
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function providerLabel(provider: string): string {
  if (provider === 'google.com') return 'Google';
  if (provider === 'apple.com') return 'Apple';
  if (provider === 'password') return 'Email + password';
  return provider;
}

function safeDisplayName(profile: UserProfile): string {
  return String(
    (profile as any)?.displayName ||
      (profile as any)?.name ||
      (profile as any)?.username ||
      'Angler',
  ).trim();
}

const AccountInfoView: React.FC<Props> = ({
  userProfile,
  isDarkMode,
  onBack,
  onLogout,
  addToast,
}) => {
  const isDark = resolveRoddoDarkMode(isDarkMode);
  const ui = getRoddoUiTokens(isDark) as any;

  const [identity, setIdentity] = useState<RoddoAccountIdentity>(() => getRoddoAccountIdentity());
  const [linkingProvider, setLinkingProvider] = useState<RoddoLinkProvider | null>(null);
  const [emailLinkOpen, setEmailLinkOpen] = useState(false);
  const [emailLinkBusy, setEmailLinkBusy] = useState(false);
  const [emailLinkEmail, setEmailLinkEmail] = useState('');
  const [emailLinkPassword, setEmailLinkPassword] = useState('');
  const [emailLinkConfirm, setEmailLinkConfirm] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  const [deletionOpen, setDeletionOpen] = useState(false);
  const [deletionBusy, setDeletionBusy] = useState(false);
  const [diagnostics, setDiagnostics] = useState<RoddoSupportDiagnostics | null>(null);

  const toast = (message: string, type: ToastType = 'info') => {
    if (addToast) addToast(message, type);
    else console.log(`[AccountInfoView:${type}]`, message);
  };

  useEffect(() => {
    const live = getRoddoAccountIdentity();
    const storedProviders = Array.isArray((userProfile as any)?.authProviders)
      ? (userProfile as any).authProviders.map(String)
      : [];

    const mergedProviders = Array.from(new Set([...live.providers, ...storedProviders]));
    const authIsSecured =
      live.isGuest === false &&
      (mergedProviders.length > 0 || Boolean(live.email));

    setIdentity({
      ...live,
      // Firebase Auth is authoritative. A stale Firestore accountType must not
      // make a successfully linked account look like a guest again.
      isGuest: authIsSecured
        ? false
        : (userProfile as any)?.accountType !== 'registered' && live.isGuest,
      providers: mergedProviders,
      email: live.email || (userProfile as any)?.email || null,
    });
  }, [userProfile]);

  useEffect(() => {
    let alive = true;
    void getRoddoSupportDiagnostics().then((next) => {
      if (alive) setDiagnostics(next);
    }).catch(() => {
      if (alive) setDiagnostics(null);
    });
    return () => {
      alive = false;
    };
  }, [userProfile.uid]);

  const providers = useMemo(
    () => Array.from(new Set((identity.providers || []).filter(Boolean))),
    [identity.providers],
  );

  const hasSecuredIdentity =
    identity.isGuest === false && (providers.length > 0 || Boolean(identity.email));
  const isGuest =
    !hasSecuredIdentity &&
    (userProfile as any)?.accountType !== 'registered' &&
    identity.isGuest;

  const email = String(identity.email || (userProfile as any)?.email || '').trim();
  const memberNumber = Number((userProfile as any)?.memberNumber || 0);
  const accountCreatedAt =
    (userProfile as any)?.createdAt ||
    (userProfile as any)?.guestCreatedAt ||
    (userProfile as any)?.memberNumberAssignedAt;
  const accessStatus = String((userProfile as any)?.accessStatus || '').toLowerCase();
  const accessLabel =
    accessStatus === 'blocked'
      ? 'Blocked'
      : accessStatus === 'waitlisted'
        ? 'Waitlisted'
        : 'Active';

  const canLinkApple = !Capacitor.isNativePlatform() || Capacitor.getPlatform() === 'ios';
  const hasPassword = providers.includes('password');

  const cardStyle: React.CSSProperties = {
    background: isDark ? '#101827' : '#FFFCF7',
    border: `1px solid ${isDark ? 'rgba(132,151,190,0.16)' : 'rgba(96,75,48,0.12)'}`,
    boxShadow: isDark ? '0 5px 14px rgba(0,0,0,0.14)' : '0 7px 18px rgba(91,65,37,0.07)',
    color: ui.textPrimary,
  };

  const subtleStyle: React.CSSProperties = {
    background: isDark ? '#0F1828' : 'rgba(255,255,255,0.68)',
    border: `1px solid ${isDark ? 'rgba(132,151,190,0.13)' : 'rgba(96,75,48,0.12)'}`,
    color: ui.textPrimary,
  };

  const quietButtonStyle: React.CSSProperties = {
    background: isDark ? '#0C1423' : 'rgba(255,255,255,0.76)',
    border: `1px solid ${isDark ? 'rgba(132,151,190,0.15)' : 'rgba(96,75,48,0.14)'}`,
    color: ui.textPrimary,
  };

  const primaryButtonStyle: React.CSSProperties = {
    background: isDark ? '#8A70F2' : '#2F80ED',
    color: '#FFFFFF',
    boxShadow: isDark ? '0 5px 14px rgba(65,52,145,0.18)' : '0 5px 14px rgba(47,128,237,0.16)',
  };

  const dangerButtonStyle: React.CSSProperties = {
    background: isDark ? 'rgba(248,113,113,0.14)' : 'rgba(254,242,242,0.96)',
    border: `1px solid ${isDark ? 'rgba(248,113,113,0.30)' : 'rgba(220,38,38,0.18)'}`,
    color: isDark ? '#FECACA' : '#991B1B',
  };

  const handleLinkProvider = async (provider: RoddoLinkProvider) => {
    if (linkingProvider) return;
    setLinkingProvider(provider);

    try {
      // Step 1 is authoritative: secure the existing Firebase UID first.
      const next = await linkRoddoAccountProvider(provider);
      const mergedProviders = Array.from(new Set([...providers, ...next.providers]));
      setIdentity({ ...next, isGuest: false, providers: mergedProviders });

      // Step 2 is metadata only. If this write fails, the account is STILL
      // secured and App.tsx will reconcile the profile on a later hydration.
      try {
        await updateUserProfile(userProfile.uid, {
          accountType: 'registered',
          authProviders: mergedProviders,
          accountUpgradedAt: (userProfile as any)?.accountUpgradedAt || Date.now(),
          updatedAt: Date.now(),
        } as any);

        toast(`${provider === 'google' ? 'Google' : 'Apple'} is now linked to this Roddo account.`, 'success');
      } catch (syncError) {
        console.warn('[AccountInfoView] provider linked; profile metadata sync deferred', syncError);
        toast('Account secured. Roddo is still syncing your account details.', 'info');
      }
    } catch (error: any) {
      // Only the provider-link operation itself is allowed to report linking as
      // failed. A later profile metadata failure must never misstate auth state.
      console.error('[AccountInfoView] provider link failed', error);
      toast(error?.message || 'Could not link that sign-in method.', 'error');
    } finally {
      setLinkingProvider(null);
    }
  };

  const handleLinkEmailPassword = async () => {
    if (emailLinkBusy || linkingProvider || hasPassword) return;

    const cleanEmail = emailLinkEmail.trim();
    if (!cleanEmail || !cleanEmail.includes('@')) {
      toast('Enter a valid email address.', 'error');
      return;
    }
    if (emailLinkPassword.length < 6) {
      toast('Password must be at least 6 characters.', 'error');
      return;
    }
    if (emailLinkPassword !== emailLinkConfirm) {
      toast('Passwords do not match.', 'error');
      return;
    }

    setEmailLinkBusy(true);
    try {
      // Authoritative step: attach the password credential to the CURRENT user.
      // This preserves the exact Firebase UID and all Roddo data under it.
      const next = await linkRoddoEmailPassword(cleanEmail, emailLinkPassword);
      const mergedProviders = Array.from(new Set([...providers, ...next.providers, 'password']));
      setIdentity({
        ...next,
        isGuest: false,
        providers: mergedProviders,
        email: next.email || cleanEmail,
      });

      setEmailLinkPassword('');
      setEmailLinkConfirm('');
      setEmailLinkOpen(false);

      try {
        await updateUserProfile(userProfile.uid, {
          accountType: 'registered',
          authProviders: mergedProviders,
          accountUpgradedAt: (userProfile as any)?.accountUpgradedAt || Date.now(),
          updatedAt: Date.now(),
        } as any);

        toast(
          next.verificationSent
            ? 'Email + password linked. Check your inbox to verify the email.'
            : 'Email + password is now linked to this Roddo account.',
          'success',
        );
      } catch (syncError) {
        console.warn('[AccountInfoView] email/password linked; profile metadata sync deferred', syncError);
        toast(
          next.verificationSent
            ? 'Account secured. Check your inbox to verify the email; Roddo is still syncing account details.'
            : 'Account secured. Roddo is still syncing your account details.',
          'info',
        );
      }
    } catch (error: any) {
      console.error('[AccountInfoView] email/password link failed', error);
      toast(error?.message || 'Could not add email + password.', 'error');
    } finally {
      setEmailLinkBusy(false);
    }
  };

  const emailLinkForm = !hasPassword && emailLinkOpen ? (
    <div className="mt-3 rounded-[14px] p-3" style={subtleStyle}>
      <div className="text-[11px] font-black">Add email + password</div>
      <p className="mt-1 text-[10px] font-semibold leading-4" style={{ color: ui.textSecondary }}>
        This secures the same Roddo account and keeps your current UID and data.
      </p>
      <div className="mt-3 grid gap-2">
        <input
          type="email"
          autoComplete="email"
          value={emailLinkEmail}
          onChange={(event) => setEmailLinkEmail(event.target.value)}
          disabled={emailLinkBusy}
          placeholder="you@example.com"
          className="roddo-mobile-form w-full rounded-[12px] px-3.5 py-3 text-sm font-semibold outline-none disabled:opacity-60"
          style={quietButtonStyle}
        />
        <input
          type="password"
          autoComplete="new-password"
          value={emailLinkPassword}
          onChange={(event) => setEmailLinkPassword(event.target.value)}
          disabled={emailLinkBusy}
          placeholder="Create password"
          className="roddo-mobile-form w-full rounded-[12px] px-3.5 py-3 text-sm font-semibold outline-none disabled:opacity-60"
          style={quietButtonStyle}
        />
        <input
          type="password"
          autoComplete="new-password"
          value={emailLinkConfirm}
          onChange={(event) => setEmailLinkConfirm(event.target.value)}
          disabled={emailLinkBusy}
          placeholder="Confirm password"
          className="roddo-mobile-form w-full rounded-[12px] px-3.5 py-3 text-sm font-semibold outline-none disabled:opacity-60"
          style={quietButtonStyle}
        />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled={emailLinkBusy}
          onClick={() => {
            setEmailLinkOpen(false);
            setEmailLinkPassword('');
            setEmailLinkConfirm('');
          }}
          className="rounded-[12px] px-3 py-2.5 text-xs font-black disabled:opacity-55"
          style={quietButtonStyle}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={emailLinkBusy || !!linkingProvider}
          onClick={() => void handleLinkEmailPassword()}
          className="rounded-[12px] px-3 py-2.5 text-xs font-black disabled:opacity-55"
          style={primaryButtonStyle}
        >
          {emailLinkBusy ? 'Adding…' : 'Add email + password'}
        </button>
      </div>
    </div>
  ) : null;

  const sendReset = async () => {
    if (!email || isSendingReset) return;
    setIsSendingReset(true);
    try {
      await sendPasswordResetEmail(auth, email);
      toast('Password reset email sent.', 'success');
    } catch (error: any) {
      console.error('[AccountInfoView] password reset failed', error);
      toast(error?.message || 'Could not send password reset email.', 'error');
    } finally {
      setIsSendingReset(false);
    }
  };

  const copyAccountId = async () => {
    try {
      await navigator.clipboard.writeText(userProfile.uid);
      toast('Account ID copied.', 'success');
    } catch {
      toast('Could not copy the account ID on this device.', 'error');
    }
  };

  const copyDiagnostics = async () => {
    if (!diagnostics) return;
    try {
      await navigator.clipboard.writeText(formatRoddoSupportDiagnostics(diagnostics));
      toast('Diagnostics copied.', 'success');
    } catch {
      toast('Could not copy diagnostics on this device.', 'error');
    }
  };

  const submitDeletion = async () => {
    const confirmed = window.confirm(
      'Submit an account deletion request? Roddo will schedule and process deletion through the secure account-deletion workflow.',
    );
    if (!confirmed) return;

    setDeletionBusy(true);
    try {
      const result = await requestAccountDeletion({
        uid: userProfile.uid,
        email: email || null,
        displayName: safeDisplayName(userProfile),
        reason: deletionReason.trim(),
      });
      const scheduledAt = Number(result?.scheduledDeletionAt || 0);
      toast(
        result?.alreadyExists
          ? 'Your deletion request is already on file.'
          : scheduledAt > 0
            ? 'Account deletion request submitted and scheduled.'
            : 'Account deletion request submitted.',
        'success',
      );
      setDeletionReason('');
      setDeletionOpen(false);
    } catch (error: any) {
      console.error('[AccountInfoView] deletion request failed', error);
      toast(error?.message || 'Could not submit the deletion request.', 'error');
    } finally {
      setDeletionBusy(false);
    }
  };

  const infoRows = [
    ['Account type', isGuest ? 'Guest' : 'Registered'],
    ['Access', accessLabel],
    ['Member number', memberNumber > 0 ? `#${memberNumber}` : 'Not assigned'],
    ['Email', email || 'Not linked'],
    ['Created', formatDate(accountCreatedAt)],
  ];

  return (
    <div
      className="mx-auto min-w-0 max-w-2xl space-y-3 px-3 pb-6 pt-4 sm:px-4"
      style={{ color: ui.textPrimary, background: ui.pageBg ?? 'transparent' }}
    >
      <section className="rounded-[16px] p-4 sm:p-5" style={cardStyle}>
        <div className="flex items-start justify-between gap-3">
          <button
            type="button"
            onClick={onBack}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-[22px] font-black leading-none transition active:translate-y-[1px]"
            style={quietButtonStyle}
            aria-label="Back"
            title="Back"
          >
            <span aria-hidden="true">‹</span>
          </button>
          <span
            className="rounded-full px-3 py-1.5 text-[11px] font-black"
            style={
              isGuest
                ? { background: 'rgba(245,158,11,0.12)', color: isDark ? '#FDE68A' : '#92400E' }
                : { background: 'rgba(16,185,129,0.12)', color: isDark ? '#A7F3D0' : '#047857' }
            }
          >
            {isGuest ? 'Guest account' : 'Account secured'}
          </span>
        </div>

        <div className="mt-5">
          <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: ui.textSecondary }}>
            Roddo account
          </div>
          <h1 className="mt-1 text-2xl font-black tracking-[-0.03em] sm:text-3xl">Account info</h1>
          <p className="mt-2 max-w-xl text-sm leading-5" style={{ color: ui.textSecondary }}>
            Sign-in methods, membership details, account security, and sensitive account controls.
          </p>
        </div>
      </section>

      {isGuest ? (
        <section className="rounded-[16px] p-4 sm:p-5" style={cardStyle}>
          <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: ui.textSecondary }}>
            Protect your catches
          </div>
          <h2 className="mt-1 text-lg font-black">Secure this guest account</h2>
          <p className="mt-1 text-xs font-semibold leading-5" style={{ color: ui.textSecondary }}>
            Link a sign-in method to keep this same UID, profile, catches, Shells, gear, crews, and history.
          </p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              disabled={emailLinkBusy || !!linkingProvider || hasPassword}
              onClick={() => {
                setEmailLinkEmail(email || '');
                setEmailLinkOpen((open) => !open);
              }}
              className="rounded-[14px] px-4 py-3 text-sm font-black transition active:translate-y-[1px] disabled:opacity-55 sm:col-span-2"
              style={primaryButtonStyle}
            >
              {hasPassword ? '✓ Email + password linked' : emailLinkOpen ? 'Close email setup' : 'Add email + password'}
            </button>
            <button
              type="button"
              disabled={emailLinkBusy || !!linkingProvider || providers.includes('google.com')}
              onClick={() => void handleLinkProvider('google')}
              className="rounded-[14px] px-4 py-3 text-sm font-black transition active:translate-y-[1px] disabled:opacity-55"
              style={providers.includes('google.com') ? subtleStyle : primaryButtonStyle}
            >
              {providers.includes('google.com') ? '✓ Google linked' : linkingProvider === 'google' ? 'Linking Google…' : 'Link Google'}
            </button>
            {canLinkApple ? (
              <button
                type="button"
                disabled={emailLinkBusy || !!linkingProvider || providers.includes('apple.com')}
                onClick={() => void handleLinkProvider('apple')}
                className="rounded-[14px] px-4 py-3 text-sm font-black transition active:translate-y-[1px] disabled:opacity-55"
                style={providers.includes('apple.com') ? subtleStyle : quietButtonStyle}
              >
                {providers.includes('apple.com') ? '✓ Apple linked' : linkingProvider === 'apple' ? 'Linking Apple…' : 'Link Apple'}
              </button>
            ) : null}
          </div>
          {emailLinkForm}
        </section>
      ) : null}

      <section className="rounded-[16px] p-4 sm:p-5" style={cardStyle}>
        <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: ui.textSecondary }}>
          Identity
        </div>
        <h2 className="mt-1 text-lg font-black">Account details</h2>
        <div className="mt-4 overflow-hidden rounded-[14px]" style={subtleStyle}>
          {infoRows.map(([label, value], index) => (
            <div
              key={label}
              className={`flex items-start justify-between gap-4 px-4 py-3 ${index ? 'border-t' : ''}`}
              style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(96,75,48,0.10)' }}
            >
              <span className="text-xs font-bold" style={{ color: ui.textSecondary }}>{label}</span>
              <span className="max-w-[65%] break-words text-right text-sm font-black">{value}</span>
            </div>
          ))}
        </div>

        <div className="mt-3 rounded-[14px] p-3" style={subtleStyle}>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.14em]" style={{ color: ui.textSecondary }}>Account ID</div>
              <div className="mt-1 truncate font-mono text-[11px] font-bold" title={userProfile.uid}>{userProfile.uid}</div>
            </div>
            <button type="button" onClick={() => void copyAccountId()} className="shrink-0 rounded-[12px] px-3 py-2 text-xs font-black" style={quietButtonStyle}>Copy</button>
          </div>
        </div>
      </section>

      <section className="rounded-[16px] p-4 sm:p-5" style={cardStyle}>
        <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: ui.textSecondary }}>
          Security
        </div>
        <h2 className="mt-1 text-lg font-black">Sign-in methods</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {providers.length ? providers.map((provider) => (
            <span key={provider} className="rounded-full px-3 py-1.5 text-[11px] font-black" style={subtleStyle}>
              ✓ {providerLabel(provider)}
            </span>
          )) : (
            <span className="rounded-full px-3 py-1.5 text-[11px] font-black" style={subtleStyle}>Guest session</span>
          )}
        </div>

        {!isGuest ? (
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              disabled={emailLinkBusy || !!linkingProvider || hasPassword}
              onClick={() => {
                setEmailLinkEmail(email || '');
                setEmailLinkOpen((open) => !open);
              }}
              className="rounded-[14px] px-4 py-3 text-sm font-black transition active:translate-y-[1px] disabled:opacity-55 sm:col-span-2"
              style={hasPassword ? subtleStyle : quietButtonStyle}
            >
              {hasPassword ? '✓ Email + password linked' : emailLinkOpen ? 'Close email setup' : 'Add email + password'}
            </button>
            <button
              type="button"
              disabled={emailLinkBusy || !!linkingProvider || providers.includes('google.com')}
              onClick={() => void handleLinkProvider('google')}
              className="rounded-[14px] px-4 py-3 text-sm font-black transition active:translate-y-[1px] disabled:opacity-55"
              style={quietButtonStyle}
            >
              {providers.includes('google.com') ? '✓ Google linked' : linkingProvider === 'google' ? 'Linking Google…' : 'Add Google'}
            </button>
            {canLinkApple ? (
              <button
                type="button"
                disabled={emailLinkBusy || !!linkingProvider || providers.includes('apple.com')}
                onClick={() => void handleLinkProvider('apple')}
                className="rounded-[14px] px-4 py-3 text-sm font-black transition active:translate-y-[1px] disabled:opacity-55"
                style={quietButtonStyle}
              >
                {providers.includes('apple.com') ? '✓ Apple linked' : linkingProvider === 'apple' ? 'Linking Apple…' : 'Add Apple'}
              </button>
            ) : null}
          </div>
        ) : null}

        {!isGuest ? emailLinkForm : null}

        {hasPassword && email ? (
          <button
            type="button"
            disabled={isSendingReset}
            onClick={() => void sendReset()}
            className="mt-2 w-full rounded-[14px] px-4 py-3 text-sm font-black transition active:translate-y-[1px] disabled:opacity-55"
            style={quietButtonStyle}
          >
            {isSendingReset ? 'Sending reset email…' : 'Send password reset email'}
          </button>
        ) : null}
      </section>

      <section className="rounded-[16px] p-4 sm:p-5" style={cardStyle}>
        <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: ui.textSecondary }}>
          Session
        </div>
        <div className="mt-2 rounded-[14px] p-3" style={subtleStyle}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-black">Roddo version</div>
              <div className="mt-0.5 text-xs font-semibold" style={{ color: ui.textSecondary }}>
                {diagnostics ? supportVersionLabel(diagnostics) : 'Loading…'}
              </div>
            </div>
            <button type="button" onClick={onLogout} className="rounded-[14px] px-4 py-3 text-sm font-black" style={quietButtonStyle}>Sign out</button>
          </div>

          <div className="mt-3 border-t pt-3" style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(96,75,48,0.10)' }}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] font-black uppercase tracking-[0.14em]" style={{ color: ui.textSecondary }}>Support diagnostics</div>
                <div className="mt-1 text-xs font-bold" style={{ color: ui.textSecondary }}>
                  {diagnostics ? `${diagnostics.platform} · ${diagnostics.runtime} · auth ${diagnostics.authBridge}` : 'Loading session details…'}
                </div>
                {diagnostics ? (
                  <div className="mt-1 truncate font-mono text-[10px] font-bold" title={diagnostics.diagnosticId}>
                    {diagnostics.diagnosticId}
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                disabled={!diagnostics}
                onClick={() => void copyDiagnostics()}
                className="shrink-0 rounded-[12px] px-3 py-2 text-xs font-black disabled:opacity-50"
                style={quietButtonStyle}
              >
                Copy
              </button>
            </div>
            <p className="mt-2 text-[10px] font-semibold leading-4" style={{ color: ui.textSecondary }}>
              Safe app/session details only. No passwords, auth tokens, exact location, or catch data.
            </p>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-[16px]" style={cardStyle}>
        <button
          type="button"
          onClick={() => setDeletionOpen((value) => !value)}
          className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left sm:px-5"
          aria-expanded={deletionOpen}
        >
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: ui.textSecondary }}>Sensitive</div>
            <div className="mt-1 text-base font-black">Account & data deletion</div>
            <p className="mt-1 text-xs leading-5" style={{ color: ui.textSecondary }}>Kept collapsed to protect destructive actions.</p>
          </div>
          <span className="grid h-9 w-9 place-items-center rounded-[12px] text-lg font-black" style={quietButtonStyle}>{deletionOpen ? '⌃' : '⌄'}</span>
        </button>

        {deletionOpen ? (
          <div className="border-t px-4 py-4 sm:px-5" style={{ borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(96,75,48,0.12)' }}>
            <div className="rounded-[14px] p-4" style={subtleStyle}>
              <p className="text-xs font-semibold leading-5" style={{ color: ui.textSecondary }}>
                Roddo uses the secure server-side deletion workflow. Some records may be retained where required for legal, safety, anti-abuse, or operational reasons.
              </p>
              <textarea
                value={deletionReason}
                onChange={(event) => setDeletionReason(event.target.value)}
                disabled={deletionBusy}
                placeholder="Optional reason for the deletion request"
                className="roddo-mobile-form mt-3 min-h-[82px] w-full resize-none rounded-[14px] px-4 py-3 text-sm outline-none"
                style={{ ...quietButtonStyle, width: '100%' }}
              />
              <button
                type="button"
                disabled={deletionBusy}
                onClick={() => void submitDeletion()}
                className="mt-3 w-full rounded-[14px] px-4 py-3 text-sm font-black transition active:translate-y-[1px] disabled:opacity-55"
                style={dangerButtonStyle}
              >
                {deletionBusy ? 'Submitting…' : 'Request account deletion'}
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default AccountInfoView;
